import {
  countsAsWarning,
  HEALTH_CHECK_SECTIONS,
  HealthCheckSection,
  IHealthCheckFinding,
  IHealthCheckInput,
  IHealthCheckRuleState,
  IHealthCheckSkippedRule,
} from './types';

/** Ein Bereich der Übersicht mit seinen Funden. */
export interface IHealthCheckSectionResult {
  section: HealthCheckSection;
  findings: IHealthCheckFinding[];
  /** Anzahl der Funde, die als Warnung zählen (`info` bleibt außen vor). */
  warningCount: number;
}

/** Ergebnis eines Health-Check-Durchlaufs. */
export interface IHealthCheckResult {
  findings: IHealthCheckFinding[];
  sections: IHealthCheckSectionResult[];
  /** Zahl, die in „mit N Warnungen gespeichert" landet (#1330). */
  warningCount: number;
  /** Reine Hinweise — angezeigt, aber nicht gezählt. */
  infoCount: number;
  /**
   * Prüfungen, die nicht laufen konnten. Leer im Normalfall; ist sie es nicht,
   * gilt das Ergebnis nur für die Prüfungen, die tatsächlich gelaufen sind.
   */
  skipped: IHealthCheckSkippedRule[];
  /** Wie viele aktive Prüfungen tatsächlich durchgelaufen sind. */
  checkedCount: number;
}

/** Standardtext, wenn eine Regel keine eigene Begründung mitbringt. */
const UNAVAILABLE_FALLBACK =
  'Die benötigten Daten waren zum Zeitpunkt der Prüfung nicht verfügbar.';

/** Text für eine Regel, die mit einer Ausnahme abgebrochen ist. */
const FAILED_REASON =
  'Die Prüfung ist mit einem Fehler abgebrochen. Bitte an den Support melden.';

/**
 * Führt den Health Check aus (#1283).
 *
 * Reine Funktion über das Vorgangs-Objekt, wie es beim Speichern gesendet würde.
 * Kein React-State, kein Netzwerk — nur so ist derselbe Code aus allen drei
 * Richtungen nutzbar: automatisch beim Speichern, manuell ausgelöst und für die
 * Anzeige im Vorgang.
 *
 * Eine Regel, die wirft, wird übersprungen statt den ganzen Durchlauf
 * abzubrechen. Ein Fehler in einer einzelnen Prüfung darf niemand am Speichern
 * hindern.
 */
export const runHealthCheck = (
  input: IHealthCheckInput,
  ruleStates: IHealthCheckRuleState[]
): IHealthCheckResult => {
  const findings: IHealthCheckFinding[] = [];
  const skipped: IHealthCheckSkippedRule[] = [];
  let checkedCount = 0;

  ruleStates
    .filter(state => state.isActive)
    .forEach(state => {
      let messages: string[] | null;

      try {
        messages = state.definition.evaluate(input, state.params);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          `Health Check: Regel "${state.ruleKey}" ist fehlgeschlagen.`,
          error
        );
        skipped.push({
          ruleKey: state.ruleKey,
          title: state.definition.title,
          reason: FAILED_REASON,
        });
        return;
      }

      // `null` heisst „kann ich nicht beurteilen" und ist etwas anderes als
      // eine leere Fundliste.
      if (messages === null) {
        skipped.push({
          ruleKey: state.ruleKey,
          title: state.definition.title,
          reason: state.definition.unavailableReason ?? UNAVAILABLE_FALLBACK,
        });
        return;
      }

      checkedCount += 1;

      messages.forEach(message => {
        findings.push({
          ruleKey: state.ruleKey,
          section: state.definition.section,
          severity: state.severity,
          message,
          tabIndex: state.definition.tabIndex,
        });
      });
    });

  const sections = HEALTH_CHECK_SECTIONS.map(section => {
    const sectionFindings = findings.filter(
      finding => finding.section === section
    );

    return {
      section,
      findings: sectionFindings,
      warningCount: sectionFindings.filter(countsAsWarning).length,
    };
  }).filter(entry => entry.findings.length > 0);

  const warningCount = findings.filter(countsAsWarning).length;

  return {
    findings,
    sections,
    warningCount,
    infoCount: findings.length - warningCount,
    skipped,
    checkedCount,
  };
};

/** Leeres Ergebnis — Zustand, solange kein Health Check gelaufen ist. */
export const EMPTY_HEALTH_CHECK_RESULT: IHealthCheckResult = {
  findings: [],
  sections: [],
  warningCount: 0,
  infoCount: 0,
  skipped: [],
  checkedCount: 0,
};
